import React, { useState, useEffect } from 'react';
import api, { getPublicUploadsOrigin } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import NotificationModal from '../components/NotificationModal';

const Cotizaciones = () => {
  const { user } = useAuth();
  const [cotizaciones, setCotizaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedCotizacion, setSelectedCotizacion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterConvocatoria, setFilterConvocatoria] = useState('');
  const [filterProveedor, setFilterProveedor] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [convocatorias, setConvocatorias] = useState([]);
  
  // Estados para notificaciones
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: null
  });

  // Cargar cotizaciones y convocatorias al montar
  useEffect(() => {
    loadCotizaciones();
    loadConvocatorias();
  }, []);

  const loadCotizaciones = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/v1/cotizaciones/');
      // Asegurar que siempre sea un array
      const data = Array.isArray(response.data) ? response.data : [];
      setCotizaciones(data);
    } catch (err) {
      console.error('Error loading cotizaciones:', err);
      setError('Error al cargar las cotizaciones');
      setCotizaciones([]);
    } finally {
      setLoading(false);
    }
  };

  const loadConvocatorias = async () => {
    try {
      const response = await api.get('/api/v1/convocatorias/');
      // Asegurar que siempre sea un array
      const data = Array.isArray(response.data) ? response.data : [];
      setConvocatorias(data);
    } catch (err) {
      console.error('Error loading convocatorias:', err);
      setConvocatorias([]);
    }
  };

  const sanitizeFragment = (value) => {
    if (!value) return '';
    const trimmed = String(value).trim().replace(/\s+/g, '_');
    return trimmed.replace(/[^a-zA-Z0-9_-]/g, '');
  };

  const fileUrl = (cotizacion, field) => {
    if (!cotizacion || !cotizacion[field]) return null;
    const folder = sanitizeFragment(cotizacion.COD_CONVOCATORIA || cotizacion.nro_convocatoria);
    return `${getPublicUploadsOrigin()}/uploads/cotizaciones/${folder}/${cotizacion[field]}`;
  };

  const handleView = async (cotizacion) => {
    try {
      const response = await api.get(`/api/v1/cotizaciones/${cotizacion.id_cotizacion}`);
      setSelectedCotizacion(response.data);
      setShowDetails(true);
    } catch (err) {
      console.error('Error loading cotizacion details:', err);
      setNotification({
        show: true,
        title: 'Error',
        message: 'Error al cargar los detalles de la cotización',
        type: 'error',
        onConfirm: null
      });
    }
  };

  // Filtrar cotizaciones
  const filteredCotizaciones = Array.isArray(cotizaciones) ? cotizaciones.filter((cot) => {
    const searchTermTrimmed = searchTerm ? searchTerm.trim() : '';
    const filterConvocatoriaTrimmed = filterConvocatoria ? filterConvocatoria.trim() : '';
    const filterProveedorTrimmed = filterProveedor ? filterProveedor.trim() : '';
    
    const matchesSearch = !searchTermTrimmed || 
      (cot.cod_cotizacion && cot.cod_cotizacion.toLowerCase().includes(searchTermTrimmed.toLowerCase())) ||
      (cot.proveedor_razon_social && cot.proveedor_razon_social.toLowerCase().includes(searchTermTrimmed.toLowerCase())) ||
      (cot.proveedor_ruc && cot.proveedor_ruc.includes(searchTermTrimmed)) ||
      (cot.COD_CONVOCATORIA && cot.COD_CONVOCATORIA.toLowerCase().includes(searchTermTrimmed.toLowerCase())) ||
      (cot.NRO_PEDIDO && String(cot.NRO_PEDIDO).includes(searchTermTrimmed)) ||
      (cot.motivo_pedido && String(cot.motivo_pedido).toLowerCase().includes(searchTermTrimmed.toLowerCase()));
    
    // El filtro de convocatoria busca por código de convocatoria (COD_CONVOCATORIA) o número
    const matchesConvocatoria = !filterConvocatoriaTrimmed || 
      (cot.COD_CONVOCATORIA && String(cot.COD_CONVOCATORIA).toLowerCase().includes(filterConvocatoriaTrimmed.toLowerCase())) ||
      (cot.nro_convocatoria && String(cot.nro_convocatoria).toLowerCase().includes(filterConvocatoriaTrimmed.toLowerCase()));
    
    const matchesProveedor = !filterProveedorTrimmed ||
      (cot.proveedor_razon_social && cot.proveedor_razon_social.toLowerCase().includes(filterProveedorTrimmed.toLowerCase())) ||
      (cot.proveedor_ruc && cot.proveedor_ruc.includes(filterProveedorTrimmed));

    const matchesEstado = !filterEstado ||
      (cot.convocatoria_estado && cot.convocatoria_estado === filterEstado);

    return matchesSearch && matchesConvocatoria && matchesProveedor && matchesEstado;
  }) : [];

  // Obtener lista única de estados para el filtro
  const getEstadosUnicos = () => {
    // Asegurar que cotizaciones sea un array
    if (!Array.isArray(cotizaciones)) {
      return [];
    }
    const estados = [...new Set(cotizaciones.map(c => c.convocatoria_estado).filter(Boolean))];
    return estados.sort();
  };

  // Obtener lista única de proveedores para sugerencias
  const getProveedoresUnicos = () => {
    // Asegurar que cotizaciones sea un array
    if (!Array.isArray(cotizaciones)) {
      return [];
    }
    const proveedores = [...new Set(
      cotizaciones
        .map(c => c.proveedor_razon_social)
        .filter(Boolean)
    )];
    return proveedores.sort();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3 text-muted">Cargando cotizaciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4" style={{ 
        padding: '1.5rem', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        borderRadius: '10px',
        color: 'white',
        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
      }}>
        <div>
          <h1 className="h3 mb-1 fw-bold" style={{ color: 'white', margin: 0 }}>
            <i className="bi bi-file-earmark-spreadsheet me-2"></i>
            Cotizaciones
          </h1>
          <p className="mb-0" style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.95rem' }}>
            Gestión y administración de cotizaciones de proveedores
          </p>
        </div>
        <button
          className="btn btn-light"
          onClick={loadCotizaciones}
          style={{
            fontWeight: '600',
            padding: '0.75rem 1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
          }}
        >
          <i className="bi bi-arrow-clockwise me-2"></i>
          Actualizar
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {/* Resumen de estadísticas */}
      {Array.isArray(cotizaciones) && cotizaciones.length > 0 && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
          gap: '1rem', 
          marginBottom: '1.5rem' 
        }}>
          <div 
            style={{
              background: 'white',
              padding: '1.25rem',
              borderRadius: '8px',
              border: '2px solid #667eea',
              textAlign: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#667eea', marginBottom: '0.25rem' }}>
              {cotizaciones.length}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: '500' }}>
              Total Cotizaciones
            </div>
          </div>
          {getEstadosUnicos().map(estado => {
            const count = Array.isArray(cotizaciones) ? cotizaciones.filter(c => c.convocatoria_estado === estado).length : 0;
            const getEstadoColor = (est) => {
              switch(est) {
                case 'ABIERTA': return { bg: '#d4edda', border: '#c3e6cb', text: '#155724' };
                case 'CERRADA': return { bg: '#fff3cd', border: '#ffeaa7', text: '#856404' };
                case 'EVALUACION': return { bg: '#d1ecf1', border: '#bee5eb', text: '#0c5460' };
                case 'FINALIZADA': return { bg: '#d1d5db', border: '#9ca3af', text: '#374151' };
                default: return { bg: '#e9ecef', border: '#dee2e6', text: '#495057' };
              }
            };
            const colors = getEstadoColor(estado);
            return (
              <div 
                key={estado}
                style={{
                  background: 'white',
                  padding: '1.25rem',
                  borderRadius: '8px',
                  border: `2px solid ${colors.border}`,
                  textAlign: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: colors.text, marginBottom: '0.25rem' }}>
                  {count}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: '500' }}>
                  {estado}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Barra de búsqueda y filtros mejorada */}
      {Array.isArray(cotizaciones) && cotizaciones.length > 0 && (
        <div style={{ 
          background: '#f8f9fa', 
          padding: '1.5rem', 
          borderRadius: '8px', 
          marginBottom: '1.5rem',
          border: '1px solid #dee2e6'
        }}>
          <div className="mb-3">
            <label className="form-label fw-semibold mb-2">
              <i className="bi bi-search me-2"></i>Búsqueda Inteligente
            </label>
            <input
              type="text"
              className="form-control form-control-lg"
              placeholder="Buscar por código de cotización, proveedor, RUC, convocatoria, pedido, motivo de pedido..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ 
                fontSize: '0.95rem',
                padding: '0.75rem 1rem'
              }}
            />
            <small className="text-muted mt-1 d-block">
              <i className="bi bi-info-circle me-1"></i>
              La búsqueda encuentra coincidencias en todos los campos principales
            </small>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <label className="form-label fw-semibold mb-2">
                <i className="bi bi-search me-2"></i>Filtrar por Convocatoria
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por código de convocatoria (ej: 01-2025)..."
                value={filterConvocatoria}
                onChange={(e) => setFilterConvocatoria(e.target.value)}
                style={{ fontSize: '0.9rem' }}
              />
              <small className="text-muted mt-1 d-block" style={{ fontSize: '0.75rem' }}>
                <i className="bi bi-info-circle me-1"></i>
                Ingrese el código de la convocatoria
              </small>
            </div>

            <div>
              <label className="form-label fw-semibold mb-2">
                <i className="bi bi-building me-2"></i>Filtrar por Proveedor
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Razón social o RUC..."
                value={filterProveedor}
                onChange={(e) => setFilterProveedor(e.target.value)}
                style={{ fontSize: '0.9rem' }}
                list="proveedores-list"
              />
              <datalist id="proveedores-list">
                {getProveedoresUnicos().slice(0, 10).map((prov, idx) => (
                  <option key={idx} value={prov} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="form-label fw-semibold mb-2">
                <i className="bi bi-flag me-2"></i>Filtrar por Estado
              </label>
              <select
                className="form-select"
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
                style={{ fontSize: '0.9rem' }}
              >
                <option value="">Todos los estados</option>
                {getEstadosUnicos().map(estado => (
                  <option key={estado} value={estado}>{estado}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setSearchTerm('');
                  setFilterConvocatoria('');
                  setFilterProveedor('');
                  setFilterEstado('');
                }}
                style={{ fontSize: '0.9rem' }}
              >
                <i className="bi bi-x-circle me-2"></i>
                Limpiar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabla de cotizaciones */}
      <div style={{ 
        background: 'white', 
        padding: '2rem', 
        borderRadius: '10px', 
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)' 
      }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h5 className="mb-1 fw-bold">Lista de Cotizaciones</h5>
            <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
              {Array.isArray(cotizaciones) && cotizaciones.length > 0 
                ? `Total: ${cotizaciones.length} cotizaciones - Mostrando: ${filteredCotizaciones.length}`
                : 'No hay cotizaciones registradas'}
            </p>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#666' }}>
            <div className="spinner-border text-primary" role="status" style={{ marginBottom: '1rem' }}>
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p style={{ fontSize: '1rem', margin: 0 }}>Cargando cotizaciones...</p>
          </div>
        ) : filteredCotizaciones.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#666' }}>
            {!Array.isArray(cotizaciones) || cotizaciones.length === 0 ? (
              <>
                <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem', display: 'block' }}></i>
                <p style={{ fontSize: '1.1rem', margin: 0 }}>No hay cotizaciones registradas aún.</p>
                <p style={{ fontSize: '0.9rem', color: '#999', marginTop: '0.5rem' }}>Las cotizaciones aparecerán aquí cuando los proveedores las envíen.</p>
              </>
            ) : (
              <>
                <i className="bi bi-search" style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem', display: 'block' }}></i>
                <p style={{ fontSize: '1.1rem', margin: 0 }}>No se encontraron cotizaciones con los filtros seleccionados.</p>
                <button
                  type="button"
                  className="btn btn-link mt-2"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterConvocatoria('');
                    setFilterProveedor('');
                    setFilterEstado('');
                  }}
                >
                  Limpiar filtros
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="table-responsive" style={{ WebkitOverflowScrolling: 'touch' }}>
            <table className="table table-hover align-middle" style={{ marginBottom: 0, minWidth: '1000px' }}>
              <thead>
                <tr style={{ background: '#667eea', color: 'white' }}>
                  <th style={{ padding: '1rem', fontWeight: '600', border: 'none' }}>ID</th>
                  <th style={{ padding: '1rem', fontWeight: '600', border: 'none' }}>Convocatoria</th>
                  <th style={{ padding: '1rem', fontWeight: '600', border: 'none' }}>Cód. Cotización</th>
                  <th style={{ padding: '1rem', fontWeight: '600', border: 'none' }}>Proveedor</th>
                  <th style={{ padding: '1rem', fontWeight: '600', border: 'none' }}>RUC</th>
                  <th style={{ padding: '1rem', fontWeight: '600', border: 'none' }}>Estado</th>
                  <th style={{ padding: '1rem', fontWeight: '600', border: 'none' }}>Fecha Registro</th>
                  <th style={{ padding: '1rem', fontWeight: '600', border: 'none', textAlign: 'center', minWidth: '150px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(filteredCotizaciones) && filteredCotizaciones.map((cot) => {
                  const getEstadoColor = (est) => {
                    switch(est) {
                      case 'ABIERTA': return { bg: '#d4edda', border: '#c3e6cb', text: '#155724' };
                      case 'CERRADA': return { bg: '#fff3cd', border: '#ffeaa7', text: '#856404' };
                      case 'EVALUACION': return { bg: '#d1ecf1', border: '#bee5eb', text: '#0c5460' };
                      case 'FINALIZADA': return { bg: '#d1d5db', border: '#9ca3af', text: '#374151' };
                      default: return { bg: '#e9ecef', border: '#dee2e6', text: '#495057' };
                    }
                  };
                  const estadoColors = getEstadoColor(cot.convocatoria_estado);
                  return (
                    <tr key={cot.id_cotizacion} style={{ transition: 'background-color 0.2s' }}>
                      <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                        <strong style={{ color: '#667eea', fontSize: '1.05rem' }}>#{cot.id_cotizacion}</strong>
                      </td>
                      <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                        <div>
                          <div style={{ fontWeight: '500' }}>
                            {cot.COD_CONVOCATORIA || `#${cot.nro_convocatoria}`}
                          </div>
                          {cot.NRO_PEDIDO && (
                            <div style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: '0.25rem' }}>
                              Pedido: {cot.NRO_PEDIDO}
                            </div>
                          )}
                          {cot.motivo_pedido && (
                            <div
                              style={{
                                fontSize: '0.8rem',
                                color: '#5a6268',
                                marginTop: '0.2rem',
                                lineHeight: 1.35,
                                maxWidth: '280px',
                              }}
                              title={cot.motivo_pedido}
                            >
                              Motivo de pedido:{' '}
                              {cot.motivo_pedido.length > 100
                                ? `${cot.motivo_pedido.substring(0, 100)}…`
                                : cot.motivo_pedido}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                        <code style={{ 
                          background: '#f8f9fa', 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          color: '#495057'
                        }}>
                          {cot.cod_cotizacion || 'N/A'}
                        </code>
                      </td>
                      <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                        <div style={{ maxWidth: '200px' }}>
                          <div style={{ fontWeight: '500' }}>{cot.proveedor_razon_social || <span className="text-muted">N/A</span>}</div>
                          {cot.proveedor_email && (
                            <div style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: '0.25rem' }}>
                              <i className="bi bi-envelope me-1"></i>
                              {cot.proveedor_email}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                        {cot.proveedor_ruc || <span className="text-muted">N/A</span>}
                      </td>
                      <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                        {cot.convocatoria_estado ? (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '0.35rem 0.75rem',
                              borderRadius: '20px',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              background: estadoColors.bg,
                              color: estadoColors.text,
                              border: `1px solid ${estadoColors.border}`
                            }}
                          >
                            {cot.convocatoria_estado}
                          </span>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                      <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                        <div style={{ fontSize: '0.9rem' }}>
                          {cot.fecha_registro 
                            ? new Date(cot.fecha_registro).toLocaleDateString('es-PE', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : <span className="text-muted">N/A</span>}
                        </div>
                      </td>
                      <td style={{ padding: '1rem', verticalAlign: 'middle', textAlign: 'center' }}>
                        <button
                          onClick={() => handleView(cot)}
                          className="btn btn-info btn-sm"
                          style={{
                            padding: '0.4rem 0.8rem',
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}
                          title="Ver detalles completos"
                        >
                          <i className="bi bi-eye"></i>
                          Ver
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Detalles */}
      {showDetails && selectedCotizacion && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '2rem',
            maxWidth: '1000px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            width: '100%'
          }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="h4 mb-1 fw-bold">Detalles de Cotización</h2>
                <p className="text-muted mb-0">#{selectedCotizacion.id_cotizacion}</p>
              </div>
              <button
                onClick={() => {
                  setShowDetails(false);
                  setSelectedCotizacion(null);
                }}
                className="btn btn-outline-secondary"
              >
                <i className="bi bi-x-circle me-2"></i>
                Cerrar
              </button>
            </div>

            {/* Información de la Cotización */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f7fafc', borderRadius: '8px' }}>
              <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Información de la Cotización</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <strong>Código de Cotización:</strong> <code>{selectedCotizacion.cod_cotizacion || 'N/A'}</code>
                </div>
                <div>
                  <strong>Plazo de Entrega:</strong> {selectedCotizacion.plazo_entrega ? `${selectedCotizacion.plazo_entrega} días` : 'N/A'}
                </div>
                <div>
                  <strong>Validez de Cotización:</strong> {selectedCotizacion.validez_cotizacion ? `${selectedCotizacion.validez_cotizacion} días` : 'N/A'}
                </div>
                <div>
                  <strong>Fecha de Registro:</strong> {selectedCotizacion.fecha_registro 
                    ? new Date(selectedCotizacion.fecha_registro).toLocaleString('es-PE')
                    : 'N/A'}
                </div>
              </div>
            </div>

            {/* Información del Proveedor */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#fff5f5', borderRadius: '8px' }}>
              <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Información del Proveedor</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <strong>Razón Social:</strong> {selectedCotizacion.proveedor_razon_social || 'N/A'}
                </div>
                <div>
                  <strong>RUC:</strong> {selectedCotizacion.proveedor_ruc || 'N/A'}
                </div>
                <div>
                  <strong>Email:</strong> {selectedCotizacion.proveedor_email || 'N/A'}
                </div>
                <div>
                  <strong>Celular:</strong> {selectedCotizacion.proveedor_celular || 'N/A'}
                </div>
              </div>
            </div>

            {/* Información de la Convocatoria */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f0fff4', borderRadius: '8px' }}>
              <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Información de la Convocatoria</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <strong>Código de Convocatoria:</strong> {selectedCotizacion.COD_CONVOCATORIA || 'N/A'}
                </div>
                <div>
                  <strong>Nro Pedido:</strong> {selectedCotizacion.NRO_PEDIDO || 'N/A'}
                </div>
                {selectedCotizacion.motivo_pedido && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <strong>Motivo de pedido:</strong>
                    <div style={{ marginTop: '0.35rem', whiteSpace: 'pre-wrap', color: '#4a5568' }}>
                      {selectedCotizacion.motivo_pedido}
                    </div>
                  </div>
                )}
                <div>
                  <strong>Rubro:</strong> {selectedCotizacion.convocatoria_rubro || 'N/A'}
                </div>
                <div>
                  <strong>Concepto:</strong> {selectedCotizacion.convocatoria_concepto || 'N/A'}
                </div>
                <div>
                  <strong>Estado:</strong> <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    background: selectedCotizacion.convocatoria_estado === 'ABIERTA' ? '#c6f6d5' :
                                selectedCotizacion.convocatoria_estado === 'CERRADA' ? '#fed7d7' :
                                selectedCotizacion.convocatoria_estado === 'EVALUACION' ? '#feebc8' :
                                selectedCotizacion.convocatoria_estado === 'FINALIZADA' ? '#bee3f8' : '#e2e8f0',
                    color: '#2d3748',
                    fontWeight: '500'
                  }}>
                    {selectedCotizacion.convocatoria_estado || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Archivos PDF */}
            <div style={{ marginBottom: '1rem' }}>
              <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Archivos Adjuntos</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ padding: '1rem', background: '#f7fafc', borderRadius: '5px', border: '1px solid #e2e8f0' }}>
                  <strong>Cotización PDF:</strong>
                  <div className="mt-2">
                    {selectedCotizacion.cotizacion_pdf ? (
                      <a 
                        href={fileUrl(selectedCotizacion, 'cotizacion_pdf')} 
                        target="_blank" 
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        <i className="bi bi-file-pdf me-1"></i>
                        Ver PDF
                      </a>
                    ) : (
                      <span className="text-muted">No adjunto</span>
                    )}
                  </div>
                </div>
                <div style={{ padding: '1rem', background: '#f7fafc', borderRadius: '5px', border: '1px solid #e2e8f0' }}>
                  <strong>Declaración de Postor PDF:</strong>
                  <div className="mt-2">
                    {selectedCotizacion.declaracion_postor_pdf ? (
                      <a 
                        href={fileUrl(selectedCotizacion, 'declaracion_postor_pdf')} 
                        target="_blank" 
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        <i className="bi bi-file-pdf me-1"></i>
                        Ver PDF
                      </a>
                    ) : (
                      <span className="text-muted">No adjunto</span>
                    )}
                  </div>
                </div>
                <div style={{ padding: '1rem', background: '#f7fafc', borderRadius: '5px', border: '1px solid #e2e8f0' }}>
                  <strong>Registro Nacional de Proveedores PDF:</strong>
                  <div className="mt-2">
                    {selectedCotizacion.registro_nacional_proveedores_pdf ? (
                      <a 
                        href={fileUrl(selectedCotizacion, 'registro_nacional_proveedores_pdf')} 
                        target="_blank" 
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        <i className="bi bi-file-pdf me-1"></i>
                        Ver PDF
                      </a>
                    ) : (
                      <span className="text-muted">No adjunto</span>
                    )}
                  </div>
                </div>
                <div style={{ padding: '1rem', background: '#f7fafc', borderRadius: '5px', border: '1px solid #e2e8f0' }}>
                  <strong>Ficha Técnica PDF:</strong>
                  <div className="mt-2">
                    {selectedCotizacion.ficha_tecnica_pdf ? (
                      <a 
                        href={fileUrl(selectedCotizacion, 'ficha_tecnica_pdf')} 
                        target="_blank" 
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        <i className="bi bi-file-pdf me-1"></i>
                        Ver PDF
                      </a>
                    ) : (
                      <span className="text-muted">No adjunto</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Notificaciones */}
      <NotificationModal
        show={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        title={notification.title}
        message={notification.message}
        type={notification.type}
        onConfirm={notification.onConfirm}
        showCancel={notification.type === 'confirm'}
      />
    </div>
  );
};

export default Cotizaciones;
